import {
  ContentStatus,
  JourneyBlockContent,
  JourneyCheckInHub,
  JourneyHub,
  JourneyInsight,
  SaveJourneyHubRequest,
} from '@addmile/backend-client';

import { DEFAULT_JOURNEY_COLOR, MILE_COLORS } from './const';

// A journey being edited. Every node carries a key so React and the tree can track nodes that
// have no id yet; a saved node's key is its id, which lets the backend's issues point at it.
export interface BlockDraft {
  key: string;
  id?: string;
  content?: JourneyBlockContent;
  checkIn?: JourneyCheckInHub;
}

export interface StepDraft {
  key: string;
  id?: string;
  title: string;
  showFeedback: boolean;
  blocks: BlockDraft[];
}

export interface MileDraft {
  key: string;
  id?: string;
  title: string;
  description: string;
  color: string;
  recap: string;
  insights: JourneyInsight[];
  steps: StepDraft[];
}

export type JourneyDraft = Omit<SaveJourneyHubRequest, 'miles'> & { miles: MileDraft[] };

// Edits run as immer recipes on the whole draft.
export type UpdateDraft = (recipe: (draft: JourneyDraft) => void) => void;

export const newJourneyDraft = (): JourneyDraft => ({
  title: '',
  imageUrl: '',
  color: DEFAULT_JOURNEY_COLOR,
  shortDescription: '',
  overview: '',
  personalization: '',
  brands: ['addmile'],
  language: 'en',
  status: ContentStatus.Draft,
  journeyComplete: { arc: { eyebrow: '', title: '' }, takeaway: { title: '', text: '' } },
  miles: [],
});

export const newMileDraft = (index: number): MileDraft => ({
  key: crypto.randomUUID(),
  title: '',
  description: '',
  color: MILE_COLORS[index % MILE_COLORS.length],
  recap: '',
  insights: [],
  steps: [],
});

export const newStepDraft = (): StepDraft => ({
  key: crypto.randomUUID(),
  title: '',
  showFeedback: false,
  blocks: [],
});

export const newCheckInDraft = (): BlockDraft => ({
  key: crypto.randomUUID(),
  checkIn: { title: '', description: '', question: '', scale: [], after: '' },
});

export const toJourneyDraft = (journey: JourneyHub): JourneyDraft => ({
  title: journey.title,
  imageUrl: journey.imageUrl,
  color: journey.color,
  shortDescription: journey.shortDescription,
  overview: journey.overview,
  personalization: journey.personalization,
  brands: journey.brands,
  language: journey.language,
  status: journey.status,
  journeyComplete: journey.journeyComplete,
  miles: journey.miles.map((mile) => ({
    key: mile.id,
    id: mile.id,
    title: mile.title,
    description: mile.description,
    color: mile.color,
    recap: mile.recap,
    insights: mile.insights,
    steps: mile.steps.map((step) => ({
      key: step.id,
      id: step.id,
      title: step.title,
      showFeedback: step.showFeedback,
      blocks: step.blocks.map((block) => ({
        key: block.id,
        id: block.id,
        content: block.content,
        checkIn: block.checkIn,
      })),
    })),
  })),
});

export const toSaveRequest = ({ miles, ...journey }: JourneyDraft): SaveJourneyHubRequest => ({
  ...journey,
  miles: miles.map((mile) => ({
    id: mile.id,
    title: mile.title,
    description: mile.description,
    color: mile.color,
    recap: mile.recap,
    insights: mile.insights,
    steps: mile.steps.map((step) => ({
      id: step.id,
      title: step.title,
      showFeedback: step.showFeedback,
      blocks: step.blocks.map(({ id, content, checkIn }) => ({
        id,
        blockSlug: content?.slug,
        checkIn,
      })),
    })),
  })),
});

// A copy starts as a new draft: without ids the backend creates fresh miles, steps and blocks.
export const toCopyRequest = (journey: JourneyHub): SaveJourneyHubRequest => {
  const draft = toJourneyDraft(journey);

  return toSaveRequest({
    ...draft,
    title: `${journey.title} (copy)`,
    status: ContentStatus.Draft,
    miles: draft.miles.map((mile) => ({
      ...mile,
      id: undefined,
      steps: mile.steps.map((step) => ({
        ...step,
        id: undefined,
        blocks: step.blocks.map((block) => ({ ...block, id: undefined })),
      })),
    })),
  });
};

export const countSteps = (journey: JourneyDraft) =>
  journey.miles.reduce((total, mile) => total + mile.steps.length, 0);

export const countBlocks = (journey: JourneyDraft) =>
  journey.miles.reduce(
    (total, mile) => total + mile.steps.reduce((sum, step) => sum + step.blocks.length, 0),
    0,
  );
